import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  $Table,
  $Row,
  $Col,
  $Card,
  $Space,
  $Button,
  $Popconfirm,
  $PageHeader,
} from "us.common/components";
import {
  makeSplitLists,
  createRenamedFile,
  getCategoryOptions,
  getRoutePath,
} from "./functions";

import { Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import { documentsManager } from "us.collection.input-output/actions";
import { MapDocumentSchema } from "./validations";
import {
  EntityTypeOptions,
  FormField,
  StorageType,
  DEFAULT_PREVIEW_TEXT,
  InitialFormValues,
  DocRow,
} from "us.collection.input-output/constants";
import { getRouteUrl } from "us.helper";
import {
  DocumentProcessingOptions,
  PreviewPanel,
  BulkActionPanel,
  DocumentDetailsColumnRender,
} from "./components";
import "./Home.scss";

type DocumentsManagerFormValues = typeof InitialFormValues;

type CaseAssignmentParams = {
  entityId: string;
  entityType: string;
  addToHistory: boolean;
  isMappedDoc: boolean;
  isSendNotification: boolean;
  documentType: string;
  keys: string[];
  storageType: StorageType;
  category: string;
  assignTo: string;
};

type EntityInfoPayload = {
  keys?: string | number | Array<string | number>;
  data?: {
    statusCode?: number;
    message?: string;
    debtorName?: string;
    creditorName?: string;
    entityId?: string;
    [key: string]: unknown;
  };
};

type MappedDocumentItem = {
  documentId?: number;
  documentID?: number;
  id?: number;
};

/**
 @description
Documents Manager - Home Component
Main container for the Documents Manager UI. Provides document listing, bulk actions,
preview panel and document processing options. Handles fetching categories, mapping,
and interactions for document assignment and preview.
Features:
- Fetch document categories on mount
- Manage document rows and selection state
- Preview documents via a preview panel
- Bulk map and assign documents
  @link https://unicorn-solutions.atlassian.net/browse/UPD-20
  @authors Keshan Nimnaka <keshana@unicorn-solutions.com>
  @since 29/06/2026
 */
const DocumentsManager: React.FC<PropsFromRedux> = (props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    getDocumentCategories,
    documentCategories,
    getEntityData,
    entityInfo,
    caseAssignment,
    caseAssignmentResult,
    assignedDocuments,
  } = props;

  const [rows, setRows] = useState<DocRow[]>([]);

  const [selectedAll, setSelectedAll] = useState(false);
  const [previewText, setPreviewText] = useState<string>(DEFAULT_PREVIEW_TEXT);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const prevUrlRef = useRef<string | null>(null);
  const splitRef = useRef<{
    open: (file: File | { originFileObj?: File } | null | undefined) => void;
  } | null>(null);
  const mountedRef = useRef(true);
  const [pendingEntityKey, setPendingEntityKey] = useState<string | null>(null);
  const [pendingEntityKeys, setPendingEntityKeys] = useState<string[]>([]);
  const formikRef = useRef<FormikProps<DocumentsManagerFormValues> | null>(
    null,
  );

  const [editingDocKey, setEditingDocKey] = useState<string | null>(null);
  const [editingDocName, setEditingDocName] = useState<string>("");

  const submittingKeysRef = useRef<Set<string>>(new Set());

  const rowStats = useMemo(() => {
    const selectedRows: DocRow[] = [];
    const unmappedRows: DocRow[] = [];
    let unsavedRowsExist = false;

    for (const row of rows) {
      if (row.checked) {
        selectedRows.push(row);
      }
      if (!row.mapped) {
        unmappedRows.push(row);
      }
      if (
        !unsavedRowsExist &&
        !row.mapped &&
        row.entityType &&
        row.assignTo &&
        (row.debtorName || row.creditorName)
      ) {
        unsavedRowsExist = true;
      }
    }

    return {
      selectedRows,
      selectedKeys: selectedRows.map((row) => row.key),
      selectedCount: selectedRows.length,
      multipleSelected: selectedRows.length > 1,
      allSelectableSelected:
        unmappedRows.length > 0 && unmappedRows.every((r) => r.checked),
      unsavedRowsExist,
    };
  }, [rows]);

  const {
    selectedRows,
    selectedKeys,
    selectedCount,
    multipleSelected,
    allSelectableSelected,
    unsavedRowsExist,
  } = rowStats;

  const documentCategoryOptions = useMemo(
    () => getCategoryOptions(documentCategories),
    [documentCategories],
  );

  const [confirmVisible, setConfirmVisible] = useState(false);

  const handleAssignToNavigation = useCallback(
    (entityType: string, assignTo: string) => {
      const route = getRoutePath(entityType, assignTo);
      if (route) {
        getRouteUrl.moduleRoute("collection", route);
      }
    },
    [],
  );

  const setBulkFormFields = useCallback(
    (values: Partial<DocumentsManagerFormValues>) => {
      if (!formikRef.current) return;
      (
        Object.entries(values) as [keyof DocumentsManagerFormValues, string][]
      ).forEach(([field, value]) => {
        formikRef.current?.setFieldValue(field, value);
      });
    },
    [],
  );

  useEffect(() => {
    getDocumentCategories && getDocumentCategories({});
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (prevUrlRef.current) {
        try {
          URL.revokeObjectURL(prevUrlRef.current);
        } catch (error) {
          console.error("DocumentsManager cleanup failed:", error);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!entityInfo || !mountedRef.current) return;

    try {
      const entityPayload = entityInfo as EntityInfoPayload;
      const keysVal = entityPayload.keys;
      const data = entityPayload.data ?? {};

      let keysArray: string[] = [];
      if (Array.isArray(keysVal)) keysArray = keysVal.map(String);
      else if (keysVal !== undefined && keysVal !== null)
        keysArray = [String(keysVal)];
      else if (pendingEntityKeys.length > 0)
        keysArray = pendingEntityKeys.slice();
      else if (pendingEntityKey) keysArray = [pendingEntityKey];

      if (!keysArray.length) return;

      const keysSet = new Set(keysArray);

      setRows((prev) =>
        prev.map((r) => {
          if (!keysSet.has(r.key)) return r;

          if (data && data.statusCode && data.statusCode !== 200) {
            return {
              ...r,
              loading: false,
              errorMessage: data.message || "Not found",
            };
          }

          const debtorName = data.debtorName ?? r.debtorName;
          const creditorName = data.creditorName ?? r.creditorName;
          const entityId = data.entityId ?? r.entityId;
          return {
            ...r,
            debtorName,
            creditorName,
            entityId,
            canConfirm: !!(debtorName || creditorName),
            loading: false,
            errorMessage: undefined,
          };
        }),
      );

      setPendingEntityKey((prev) => (prev && keysSet.has(prev) ? null : prev));
      setPendingEntityKeys((prev) => prev.filter((k) => !keysSet.has(k)));
    } catch (error) {
      console.error("DocumentsManager: failed to update entityInfo", error);
    }
  }, [entityInfo, pendingEntityKey, pendingEntityKeys]);

  useEffect(() => {
    if (!mountedRef.current) return;

    try {
      const rawKeys = caseAssignmentResult?.keys;
      let keysArray: string[] = [];
      if (Array.isArray(rawKeys)) keysArray = rawKeys.map((k) => String(k));
      else if (rawKeys !== undefined && rawKeys !== null)
        keysArray = [String(rawKeys)];

      const { assignTo, category } =
        (caseAssignmentResult?.data as {
          assignTo?: string;
          category?: string;
        }) ?? {};
      if (!keysArray.length) return;

      const keysSet = new Set(keysArray);

      setRows((prev) =>
        prev.map((r) =>
          keysSet.has(String(r.key))
            ? {
                ...r,
                mapped: true,
                loading: false,
                checked: false,
                assignTo: assignTo ?? r.assignTo,
                category: category ?? r.category,
              }
            : r,
        ),
      );

      setBulkFormFields({
        [FormField.BulkAssignTo]: "",
        [FormField.BulkCategory]: "",
        [FormField.BulkEntityType]: "",
        [FormField.BulkDebtorName]: "",
        [FormField.BulkCreditorName]: "",
      });
      setSelectedAll(false);
    } catch (error) {
      console.error(
        "DocumentsManager: failed to update case assignment",
        error,
      );
    }
  }, [caseAssignmentResult, setBulkFormFields]);

  useEffect(() => {
    setSelectedAll(allSelectableSelected);
  }, [allSelectableSelected]);

  useEffect(() => {
    const getCommonValue = (
      field: Extract<
        keyof DocRow,
        "assignTo" | "entityType" | "category" | "debtorName" | "creditorName"
      >,
    ): string => {
      if (!selectedRows.length) return "";
      const values = selectedRows.map((s) => String(s[field] ?? ""));
      return values.every((value) => value === values[0]) ? values[0] : "";
    };

    if (!formikRef.current) return;

    if (selectedRows.length === 0) {
      setBulkFormFields({
        [FormField.BulkAssignTo]: "",
        [FormField.BulkEntityType]: "",
        [FormField.BulkCategory]: "",
        [FormField.BulkDebtorName]: "",
        [FormField.BulkCreditorName]: "",
      });
    } else {
      setBulkFormFields({
        [FormField.BulkAssignTo]: getCommonValue("assignTo"),
        [FormField.BulkEntityType]: getCommonValue("entityType"),
        [FormField.BulkCategory]: getCommonValue("category"),
        [FormField.BulkDebtorName]: getCommonValue("debtorName"),
        [FormField.BulkCreditorName]: getCommonValue("creditorName"),
      });
    }
  }, [selectedRows, setBulkFormFields]);

  const updateRow = useCallback((key: string, patch: Partial<DocRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r)),
    );
  }, []);

  const updateRowsByKeys = useCallback(
    (keys: string[], patch: Partial<DocRow>) => {
      const keysSet = new Set(keys);
      setRows((prev) =>
        prev.map((r) => (keysSet.has(r.key) ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const startEditingDocName = useCallback((rec: DocRow) => {
    setEditingDocKey(String(rec.key));
    setEditingDocName(rec.documentName);
  }, []);

  const saveDocName = useCallback(
    (key: string | number) => {
      const trimmedName = editingDocName.trim();

      if (trimmedName) {
        const stringKey = String(key);

        setRows((prev) =>
          prev.map((r) => {
            if (String(r.key) !== stringKey) return r;

            let updatedFile = r.file;

            if (r.file && r.file instanceof File) {
              updatedFile = createRenamedFile(r.file, trimmedName);
            }

            return {
              ...r,
              documentName: trimmedName,
              file: updatedFile,
            };
          }),
        );
      }

      setEditingDocKey(null);
      setEditingDocName("");
    },
    [editingDocName],
  );

  const cancelEditDocName = () => {
    setEditingDocKey(null);
    setEditingDocName("");
  };

  const performBulkGetEntityData = useCallback(
    (bulkAssignTo?: string) => {
      if (!selectedKeys.length) return;
      const assignTo =
        bulkAssignTo ?? formikRef.current?.values?.bulkAssignTo ?? "";
      const entityType = formikRef.current?.values?.bulkEntityType ?? "";
      if (!assignTo || !entityType) return;

      const selectedKeySet = new Set(selectedKeys);
      setRows((prev) =>
        prev.map((r) =>
          selectedKeySet.has(r.key) ? { ...r, loading: true } : r,
        ),
      );

      setPendingEntityKeys(selectedKeys);
      try {
        getEntityData &&
          getEntityData({ assignTo, entityType, key: selectedKeys });
      } catch (error) {
        if (!mountedRef.current) return;
        setRows((prev) =>
          prev.map((r) =>
            selectedKeySet.has(r.key) ? { ...r, loading: false } : r,
          ),
        );
        setPendingEntityKeys((prev) =>
          prev.filter((k) => !selectedKeySet.has(k)),
        );
        console.error("DocumentsManager: failed to fetch entity data", error);
      }
    },
    [getEntityData, selectedKeys],
  );

  const handleSingleMap = useCallback(
    async (rec: DocRow) => {
      if (!rec) return;
      const assignTo = rec.assignTo ?? "";
      const entityType = rec.entityType ?? "";
      const entityId = rec.entityId ?? "";
      const category = rec.category ?? "";
      if (!assignTo || !entityType) return;

      setRows((prev) =>
        prev.map((r) => (r.key === rec.key ? { ...r, loading: true } : r)),
      );

      const fileListToUpload: File[] = [];
      if (rec.file instanceof File) fileListToUpload.push(rec.file);

      const params: CaseAssignmentParams = {
        entityId,
        entityType,
        addToHistory: true,
        isMappedDoc: true,
        isSendNotification: (caseAssignmentResult?.keys?.length ?? 0) === 0,
        documentType: category,
        keys: [rec.key],
        storageType: StorageType.BlobStorage,
        category,
        assignTo,
      };

      caseAssignment && caseAssignment(fileListToUpload, params);
    },
    [caseAssignment, caseAssignmentResult?.keys],
  );

  const handleBulkMap = useCallback(async () => {
    const selected = selectedRows;
    if (!selected.length) return;
    const assignTo = formikRef.current?.values?.bulkAssignTo ?? "";
    const entityId = entityInfo?.data?.entityId ?? "";
    const entityType = formikRef.current?.values?.bulkEntityType ?? "";
    const category = formikRef.current?.values?.bulkCategory ?? "";
    if (!assignTo || !entityType) return;

    const docIds = selected.map((s) => s.key);
    const docIdSet = new Set(docIds);

    setRows((prev) =>
      prev.map((r) => (docIdSet.has(r.key) ? { ...r, loading: true } : r)),
    );

    const fileListToUpload: File[] = [];
    selected.forEach((s) => {
      if (s.file instanceof File) fileListToUpload.push(s.file);
    });

    const params: CaseAssignmentParams = {
      entityId,
      entityType,
      addToHistory: true,
      isMappedDoc: true,
      isSendNotification: (caseAssignmentResult?.keys?.length ?? 0) === 0,
      documentType: category,
      keys: docIds,
      storageType: StorageType.BlobStorage,
      category,
      assignTo,
    };

    caseAssignment && caseAssignment(fileListToUpload, params);
  }, [
    caseAssignment,
    caseAssignmentResult?.keys,
    entityInfo?.data?.entityId,
    selectedRows,
  ]);

  const handleSaveAll = useCallback(() => {
    const rowsToMap = rows.filter(
      (r) =>
        !r.mapped &&
        !!r.entityType &&
        !!r.assignTo &&
        (!!r.debtorName || !!r.creditorName),
    );

    if (!rowsToMap.length) return;

    const rowsToMapSet = new Set(rowsToMap.map((row) => row.key));
    setRows((prev) =>
      prev.map((r) => (rowsToMapSet.has(r.key) ? { ...r, loading: true } : r)),
    );

    rowsToMap.forEach((row) => {
      const entityId = row.entityId ?? entityInfo?.data?.entityId ?? "";
      const entityType = row.entityType ?? "";
      const category = row.category ?? "";
      const assignTo = row.assignTo ?? "";
      if (!entityId || !entityType || !assignTo) return;

      const fileListToUpload: File[] = [];
      if (row.file instanceof File) fileListToUpload.push(row.file);

      const params: CaseAssignmentParams = {
        entityId,
        entityType,
        addToHistory: true,
        isMappedDoc: true,
        isSendNotification: (caseAssignmentResult?.keys?.length ?? 0) === 0,
        documentType: category,
        keys: [row.key],
        storageType: StorageType.BlobStorage,
        category,
        assignTo,
      };

      caseAssignment && caseAssignment(fileListToUpload, params);
    });
  }, [rows, entityInfo, caseAssignment, caseAssignmentResult?.keys]);

  const deleteCheckedRows = () => {
    setRows((prev) => prev.filter((r) => !r.checked));
    setSelectedAll(false);
  };

  const resetAllRows = () => {
    try {
      const mappedArr = (caseAssignmentResult?.mappedDocumentsIds ??
        []) as MappedDocumentItem[];
      const documentIds = mappedArr
        .map((m) => m && (m.documentId ?? m.documentID ?? m.id))
        .filter((d): d is number => d !== undefined && d !== null)
        .map((d) => Number(d));

      if (documentIds && documentIds.length > 0 && assignedDocuments) {
        try {
          assignedDocuments({ documentIds });
        } catch (error) {
          console.error(
            "DocumentsManager: assignedDocuments reset failed",
            error,
          );
        }
      }
    } catch (error) {
      console.error(
        "DocumentsManager: failed to reset assigned documents",
        error,
      );
    }

    try {
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          entityType: "",
          assignTo: "",
          debtorName: "",
          creditorName: "",
          category: "",
          mapped: false,
          canConfirm: false,
          loading: false,
          checked: false,
        })),
      );
    } catch (error) {
      console.error("DocumentsManager: failed to clear rows", error);
      setRows([]);
    }

    setSelectedAll(false);

    if (prevUrlRef.current) {
      try {
        URL.revokeObjectURL(prevUrlRef.current);
      } catch (error) {
        console.error("DocumentsManager: failed to revoke preview URL", error);
      }
      prevUrlRef.current = null;
    }
    setPreviewUrl(null);
    setPreviewText(DEFAULT_PREVIEW_TEXT);
    try {
      setBulkFormFields({
        [FormField.BulkAssignTo]: "",
        [FormField.BulkCategory]: "",
        [FormField.BulkEntityType]: "",
        [FormField.BulkDebtorName]: "",
        [FormField.BulkCreditorName]: "",
      });
    } catch (error) {
      console.error(
        "DocumentsManager: failed to clear bulk form values",
        error,
      );
    }

    setPendingEntityKey(null);
    setPendingEntityKeys([]);
  };

  const columns = React.useMemo(
    () =>
      DocumentDetailsColumnRender({
        t,
        selectedAll,
        setSelectedAll,
        setRows,
        updateRow,
        editingDocKey,
        editingDocName,
        setEditingDocName,
        startEditingDocName,
        saveDocName,
        cancelEditDocName,
        multipleSelected,
        EntityTypeOptions,
        documentCategories,
        getEntityData,
        submittingKeysRef,
        rows,
        handleSingleMap,
        handleAssignToNavigation,
        caseAssignmentResult,
        entityInfo,
        assignedDocuments,
        prevUrlRef,
        setPreviewUrl,
        setPreviewText,
      }),
    [
      t,
      selectedAll,
      rows,
      editingDocKey,
      editingDocName,
      multipleSelected,
      documentCategories,
      caseAssignmentResult,
      entityInfo,
      saveDocName,
      setEditingDocName,
    ],
  );

  return (
    <Formik
      innerRef={formikRef}
      enableReinitialize
      validationSchema={MapDocumentSchema}
      initialValues={{
        ...InitialFormValues,
      }}
      onSubmit={() => {}}
    >
      {({ values, setFieldValue }) => (
        <div className="document-management home">
          <div className="page-header header-border pr-0 d-flex flex-row align-items-center justify-content-between mdh-header">
            {unsavedRowsExist ? (
              <$Popconfirm
                title={t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.CONFIRM_SAVE_BEFORE_LEAVING",
                )}
                onConfirm={() => {
                  setConfirmVisible(false);
                  handleSaveAll();
                }}
                onCancel={() => {
                  setConfirmVisible(false);
                  navigate("/documents-manager-history");
                }}
                okText={t("US.COMMON:COMMON.YES")}
                cancelText={t("US.COMMON:COMMON.NO")}
                open={confirmVisible}
                visible={confirmVisible}
              >
                <$PageHeader
                  className="site-page-header"
                  title={t(
                    "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.IMPORT_AND_PROCESS_DOCUMENTS",
                  )}
                  onBack={() => {
                    setConfirmVisible(true);
                  }}
                />
              </$Popconfirm>
            ) : (
              <$PageHeader
                className="site-page-header"
                title={t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.IMPORT_AND_PROCESS_DOCUMENTS",
                )}
                onBack={() => {
                  navigate("/documents-manager-history");
                }}
              />
            )}
          </div>

          <$Row gutter={16} className="home-row">
            <$Col span={24}>
              <$Card bordered={false} className="home-card-transparent">
                <$Row gutter={16}>
                  <$Col span={8}>
                    <DocumentProcessingOptions
                      splitRef={splitRef}
                      onFilesSplit={(files: File[]) => {
                        const { rows: newRows, fileList: newFileList } =
                          makeSplitLists(files || []);

                        setRows((prev) => [...prev, ...newRows]);
                      }}
                    />
                  </$Col>

                  <$Col span={16}>
                    <BulkActionPanel
                      selectedCount={selectedCount}
                      selectedKeys={selectedKeys}
                      values={values}
                      setFieldValue={setFieldValue}
                      documentCategoryOptions={documentCategoryOptions}
                      entityInfo={entityInfo}
                      performBulkGetEntityData={performBulkGetEntityData}
                      handleBulkMap={handleBulkMap}
                      updateRowsByKeys={updateRowsByKeys}
                    />
                  </$Col>
                </$Row>
              </$Card>
            </$Col>
          </$Row>

          <$Row gutter={16} className="home-main-row">
            <$Col span={15}>
              <$Row className="home-table-actions-row">
                <$Space>
                  <$Popconfirm
                    title={t(
                      "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.CONFIRM_SAVE_ALL",
                    )}
                    onConfirm={handleSaveAll}
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                  >
                    <$Button disabled={!unsavedRowsExist}>
                      {t(
                        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.SAVE_ALL",
                      )}
                    </$Button>
                  </$Popconfirm>
                  <$Button
                    onClick={resetAllRows}
                    disabled={!caseAssignmentResult?.mappedDocumentsIds?.length}
                  >
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.RESET_ALL",
                    )}
                  </$Button>
                  <$Button
                    danger
                    className="home-delete-all-btn"
                    disabled={selectedCount < 2}
                    onClick={deleteCheckedRows}
                  >
                    {t(
                      "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.DELETE_ALL",
                    )}
                  </$Button>
                </$Space>
              </$Row>
              <$Table
                columns={columns}
                dataSource={rows}
                pagination={false}
                rowKey="key"
              />
            </$Col>
            <$Col span={9}>
              <$Card className="home-preview-card">
                <PreviewPanel
                  previewUrl={previewUrl}
                  previewText={previewText}
                />
              </$Card>
            </$Col>
          </$Row>
        </div>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { documentsManager } = state;
  const { documentCategories, entityInfo } = documentsManager;

  return {
    documentCategories: documentCategories?.data,
    entityInfo,
    caseAssignmentResult: documentsManager.caseAssignmentResult,
  };
};

const mapDispatchToProps = {
  getDocumentCategories: documentsManager.documentCategories.get,
  getEntityData: documentsManager.entityInfo.get,
  caseAssignment: documentsManager.caseAssignment.upload,
  assignedDocuments: documentsManager.assignedDocuments.reset,
};
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DocumentsManager);
